import { ArrowUpIcon } from "@phosphor-icons/react/dist/csr/ArrowUp";
import { StopIcon } from "@phosphor-icons/react/dist/csr/Stop";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import {
  AttachButton,
  EditorAttachments,
} from "@/payroll/editor/editor-documents";
import { useAttachmentInput } from "@/payroll/editor/editor-documents/use-attachment-input";
import type { EditorUpload } from "@/payroll/editor/editor-documents/use-editor-upload";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { ComposerInput } from "./composer-input";
import { QueuedMessages } from "./queued-messages";
import type { Composer } from "./use-composer";
import type { Outgoing } from "./use-outgoing";

/** Attachments, the request and queue controls share one bounded composer. */
export const EditorComposer = ({
  composer,
  documents,
  remove,
  upload,
  editable,
  disabled,
  open,
  busy,
  canStop,
  stopping,
  onStop,
  stopLabel,
  onSubmit,
  queued,
  onQueueSendNow,
  onQueueEdit,
  onQueueRemove,
}: {
  composer: Composer;
  documents: PayrollEditorQueries["documents"];
  remove: PayrollEditorQueries["remove"];
  upload: EditorUpload;
  editable: boolean;
  disabled: boolean;
  open: PayrollEditorQueries["open"];
  /** A request is running, so sending queues the message instead. */
  busy: boolean;
  canStop: boolean;
  stopping: boolean;
  onStop: () => void;
  stopLabel: "Stop" | "Cancel";
  onSubmit: () => void;
  /** Written while a request was running, waiting their turn. */
  queued: Outgoing[];
  onQueueSendNow: (id: string) => void;
  onQueueEdit: (id: string, message: string) => void;
  onQueueRemove: (id: string) => void;
}) => {
  const { dragging, ...attachmentInput } = useAttachmentInput(upload);
  if (!editable)
    return (
      <Stack
        gap={0}
        align="stretch"
        direction="row"
        className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3"
      >
        <p className="text-subtle">
          This proposal is closed. Start a new one to keep going.
        </p>
        {/* In place. Volca publishing a proposal should not move the reader to
            a different screen with a different address; the same screen simply
            has a new proposal on it. */}
        <Button
          type="button"
          size="xs"
          variant="outline"
          loading={open.isPending}
          onClick={() => open.mutate()}
        >
          Start a new proposal
        </Button>
      </Stack>
    );
  const hasText = !!composer.text.trim();
  const canSend =
    !disabled && hasText && composer.text.length <= EDITOR_LIMITS.messageLength;
  return (
    <form
      // No padding above the input. The transcript scrolls right into it and
      // fades at the seam; four pixels of ground between the two turned that
      // fade into a cut line with a grey band under it.
      className="relative flex shrink-0 flex-col gap-2 px-3 pt-1 pb-3"
      {...attachmentInput}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSubmit();
      }}
    >
      <QueuedMessages
        queued={queued}
        canSend={!disabled}
        onSendNow={onQueueSendNow}
        onEdit={onQueueEdit}
        onRemove={onQueueRemove}
      />

      {/* The whole box is the field. Most of it is padding and controls, and a
          click that landed on any of that used to do nothing, so the box read
          as inert. `mousedown` rather than `click`, so the caret arrives before
          the press completes; `preventDefault` keeps the browser from moving
          focus somewhere else on the way. Clicks on the real controls are left
          alone — the attach button and send must keep their own behaviour. */}
      <div
        className={cn(
          "relative rounded-2xl border border-line bg-surface-raised shadow-raised transition-colors focus-within:border-line focus-within:ring-3 focus-within:ring-focus/50",
          { "border-brand-line ring-1 ring-focus": dragging },
        )}
        onMouseDown={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest("button, a, input, textarea, [role='button']")
          )
            return;
          event.preventDefault();
          composer.field.current?.focus();
        }}
      >
        <EditorAttachments
          documents={documents}
          remove={remove}
          upload={upload}
        />
        <ComposerInput
          composer={composer}
          busy={busy}
          canSend={canSend}
          onSubmit={onSubmit}
        >
          <AttachButton upload={upload} />
          {(!busy || hasText) && (
            <Button
              type="submit"
              size="icon-sm"
              variant={canSend ? "default" : "secondary"}
              className="shrink-0 rounded-full"
              disabled={!canSend}
              aria-label={busy ? "Queue request" : "Send request"}
              title={
                busy
                  ? "Queue this. It sends when the current request finishes."
                  : "Send request"
              }
            >
              <ArrowUpIcon className="size-3.5" />
            </Button>
          )}
          {busy && (
            <Button
              type="button"
              size="icon-sm"
              className="shrink-0 rounded-full"
              disabled={!canStop || stopping}
              aria-label={
                stopping ? "Stopping request" : `${stopLabel} request`
              }
              aria-busy={stopping}
              title={stopping ? "Stopping request…" : `${stopLabel} request`}
              onClick={onStop}
            >
              <StopIcon aria-hidden weight="fill" className="size-3.5" />
            </Button>
          )}
        </ComposerInput>
      </div>

      {/* Explain queuing beside the input while a request is running. */}
      {busy && (
        <p className="px-1 text-xs text-subtle">
          Your next message will wait until this request finishes.
        </p>
      )}
    </form>
  );
};
